/**
 * Formats a customer phone number for display using custom, dependency-free logic.
 *
 * Numbers are normalised (the `00` international dialing prefix becomes `+`) and grouped by the
 * country code found in the number, e.g.:
 *   - UK (+44):    "+44 7911 123456"   (national number grouped 4-6, trunk-prefix 0 dropped)
 *   - Spain (+34): "+34 612 34 56 78"  (national number grouped 3-2-2-2)
 *   - US/CA (+1):  "+1 201 555 0123"   (national number grouped 3-3-4)
 *
 * A number with no international prefix (`+` or `00`) has no country code to read, so we assume
 * US/Canada — both share the +1 country code and are told apart by area code. This covers bare
 * national numbers like `2015550123`, `(201) 555-0123`, or `1 201 555 0123`.
 *
 * Anything we can't confidently group (unknown country code, wrong digit count, non-numeric) is
 * returned unchanged, so a number is never mangled or hidden.
 */
export const formatPhoneNumber = (phone?: string | null): string => {
  const raw = (phone ?? '').trim()
  if (!raw) return ''

  const hasInternationalPrefix = raw.startsWith('+') || raw.startsWith('00')
  // libphonenumber-free: normalise the 00 dialing prefix to + so parsing only handles one form.
  const normalized = raw.startsWith('00') ? `+${raw.slice(2)}` : raw
  const digits = normalized.replace(/\D/g, '')

  // Spain: +34, 9-digit national number grouped 3-2-2-2.
  if (normalized.startsWith('+34')) {
    const national = digits.slice(2)
    if (national.length === 9) {
      return `+34 ${national.slice(0, 3)} ${national.slice(3, 5)} ${national.slice(5, 7)} ${national.slice(7)}`
    }
    return raw
  }

  // UK: +44, drop the trunk-prefix 0, 10-digit national number grouped 4-6.
  if (normalized.startsWith('+44')) {
    let national = digits.slice(2)
    if (national.startsWith('0')) national = national.slice(1)
    if (national.length === 10) {
      return `+44 ${national.slice(0, 4)} ${national.slice(4)}`
    }
    return raw
  }

  // US/Canada: explicit +1, or any number with no international prefix. Group as NANP by digit
  // count (10, or 11 with a leading 1). Both countries share +1 and differ only by area code.
  if (normalized.startsWith('+1') || !hasInternationalPrefix) {
    let national = normalized.startsWith('+1') ? digits.slice(1) : digits
    if (national.length === 11 && national.startsWith('1')) national = national.slice(1)
    if (national.length === 10) {
      return `+1 ${national.slice(0, 3)} ${national.slice(3, 6)} ${national.slice(6)}`
    }
    return raw
  }

  return raw
}
